// Input Validation and Sanitization Utility
import DOMPurify from 'dompurify';

class InputValidator {
  // Email validation
  static validateEmail(email) {
    if (!email || typeof email !== 'string') {
      return { isValid: false, error: 'Email is required and must be a string' };
    }
    
    const trimmedEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(trimmedEmail)) {
      return { isValid: false, error: 'Invalid email format' };
    }
    
    if (trimmedEmail.length > 254) {
      return { isValid: false, error: 'Email is too long' };
    }
    
    return { isValid: true, value: trimmedEmail };
  }

  static WEAK_PASSWORDS = [
    'password',
    '123456',
    '12345678',
    'qwerty',
    'admin',
    'user',
    'letmein',
    'welcome',
    'monkey',
    'dragon',
  ];

  // Password validation (legacy flows: login, signup, profile change dialog)
  static validatePassword(password) {
    if (!password || typeof password !== 'string') {
      return { isValid: false, error: 'Password is required and must be a string' };
    }
    
    if (password.length < 6) {
      return { isValid: false, error: 'Password must be at least 6 characters long' };
    }
    
    if (password.length > 128) {
      return { isValid: false, error: 'Password is too long' };
    }
    
    if (this.WEAK_PASSWORDS.includes(password.toLowerCase())) {
      return { isValid: false, error: 'Password is too weak' };
    }
    
    return { isValid: true, value: password };
  }

  // Stricter rules for first-login / new member password setup
  static validateNewMemberPassword(password) {
    if (!password || typeof password !== 'string') {
      return { isValid: false, errorCode: 'required', error: 'Password is required' };
    }

    if (password.length < 8) {
      return { isValid: false, errorCode: 'min_length', error: 'Password must be at least 8 characters long' };
    }

    if (password.length > 128) {
      return { isValid: false, errorCode: 'max_length', error: 'Password is too long' };
    }

    if (!/[a-zA-Z]/.test(password)) {
      return { isValid: false, errorCode: 'needs_letter', error: 'Password must contain at least one letter' };
    }

    if (!/\d/.test(password)) {
      return { isValid: false, errorCode: 'needs_number', error: 'Password must contain at least one number' };
    }

    if (this.WEAK_PASSWORDS.includes(password.toLowerCase())) {
      return { isValid: false, errorCode: 'too_weak', error: 'Password is too weak' };
    }

    return { isValid: true, value: password };
  }

  // Name validation
  static validateName(name) {
    if (!name || typeof name !== 'string') {
      return { isValid: false, error: 'Name is required and must be a string' };
    }
    
    const trimmedName = name.trim();
    
    if (trimmedName.length < 2) {
      return { isValid: false, error: 'Name must be at least 2 characters long' };
    }
    
    if (trimmedName.length > 100) {
      return { isValid: false, error: 'Name is too long' };
    }
    
    // Allow letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/;
    if (!nameRegex.test(trimmedName)) {
      return { isValid: false, error: 'Name contains invalid characters' };
    }
    
    // Require at least one space (first name + surname)
    if (!trimmedName.includes(' ')) {
      return { isValid: false, error: 'Please enter your full name (first name and surname)' };
    }
    
    // Ensure the name has meaningful parts (not just spaces)
    const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);
    if (nameParts.length < 2) {
      return { isValid: false, error: 'Please enter both your first name and surname' };
    }
    
    return { isValid: true, value: trimmedName };
  }

  // Text content validation and sanitization
  static validateAndSanitizeText(text, maxLength = 1000) {
    if (!text || typeof text !== 'string') {
      return { isValid: false, error: 'Text is required and must be a string' };
    }
    
    if (text.length > maxLength) {
      return { isValid: false, error: `Text is too long (maximum ${maxLength} characters)` };
    }
    
    // Sanitize HTML content
    const sanitizedText = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: []
    });
    
    return { isValid: true, value: sanitizedText };
  }

  // HTML content validation and sanitization (for rich text)
  static validateAndSanitizeHTML(html, maxLength = 10000) {
    if (!html || typeof html !== 'string') {
      return { isValid: false, error: 'HTML content is required and must be a string' };
    }
    
    if (html.length > maxLength) {
      return { isValid: false, error: `HTML content is too long (maximum ${maxLength} characters)` };
    }
    
    // Sanitize HTML with allowed tags and attributes
    const sanitizedHTML = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'div', 'span',
        'strong', 'b', 'em', 'i', 'u',
        'ul', 'ol', 'li',
        'blockquote', 'code', 'pre',
        'a', 'img'
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'class',
        'id', 'style', 'target'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
    });
    
    return { isValid: true, value: sanitizedHTML };
  }

  // URL validation
  static validateURL(url) {
    if (!url || typeof url !== 'string') {
      return { isValid: false, error: 'URL is required and must be a string' };
    }
    
    const trimmedURL = url.trim();
    
    try {
      const urlObj = new URL(trimmedURL);
      const allowedProtocols = ['http:', 'https:'];
      
      if (!allowedProtocols.includes(urlObj.protocol)) {
        return { isValid: false, error: 'URL must use HTTP or HTTPS protocol' };
      }
      
      return { isValid: true, value: trimmedURL };
    } catch (error) {
      return { isValid: false, error: 'Invalid URL format' };
    }
  }

  // Phone number validation
  static validatePhoneNumber(phone) {
    if (!phone || typeof phone !== 'string') {
      return { isValid: false, error: 'Phone number is required and must be a string' };
    }
    
    const cleanedPhone = phone.replace(/\D/g, '');
    
    if (cleanedPhone.length < 10 || cleanedPhone.length > 15) {
      return { isValid: false, error: 'Phone number must be between 10 and 15 digits' };
    }
    
    return { isValid: true, value: cleanedPhone };
  }

  // Date validation
  static validateDate(date) {
    if (!date) {
      return { isValid: false, error: 'Date is required' };
    }
    
    const dateObj = new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return { isValid: false, error: 'Invalid date format' };
    }
    
    // Check if date is not in the future (for most use cases)
    if (dateObj > new Date()) {
      return { isValid: false, error: 'Date cannot be in the future' };
    }
    
    return { isValid: true, value: dateObj };
  }

  // Number validation
  static validateNumber(number, min = null, max = null) {
    if (number === null || number === undefined) {
      return { isValid: false, error: 'Number is required' };
    }
    
    const num = Number(number);
    
    if (isNaN(num)) {
      return { isValid: false, error: 'Invalid number format' };
    }
    
    if (min !== null && num < min) {
      return { isValid: false, error: `Number must be at least ${min}` };
    }
    
    if (max !== null && num > max) {
      return { isValid: false, error: `Number must be at most ${max}` };
    }
    
    return { isValid: true, value: num };
  }

  // File validation
  static validateFile(file, allowedTypes = [], maxSize = 5 * 1024 * 1024) { // 5MB default
    if (!file) {
      return { isValid: false, error: 'File is required' };
    }
    
    if (!(file instanceof File)) {
      return { isValid: false, error: 'Invalid file object' };
    }
    
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      return { isValid: false, error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}` };
    }
    
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      return { isValid: false, error: `File size too large. Maximum size: ${maxSizeMB}MB` };
    }
    
    return { isValid: true, value: file };
  }

  // Generic string validation
  static validateString(str, minLength = 1, maxLength = 255, pattern = null) {
    if (!str || typeof str !== 'string') {
      return { isValid: false, error: 'String is required and must be a string' };
    }
    
    const trimmedStr = str.trim();
    
    if (trimmedStr.length < minLength) {
      return { isValid: false, error: `String must be at least ${minLength} characters long` };
    }
    
    if (trimmedStr.length > maxLength) {
      return { isValid: false, error: `String is too long (maximum ${maxLength} characters)` };
    }
    
    if (pattern && !pattern.test(trimmedStr)) {
      return { isValid: false, error: 'String format is invalid' };
    }
    
    return { isValid: true, value: trimmedStr };
  }

  // Sanitize object recursively
  static sanitizeObject(obj) {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item));
    }
    
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        sanitized[key] = DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      } else {
        sanitized[key] = this.sanitizeObject(value);
      }
    }
    
    return sanitized;
  }
}

export default InputValidator;

